/** The table: the contract, the melds, the piles, your hand. scope.md §8.1. */

import { useEffect, useMemo, useState } from 'react';
import type { Card, GameEvent, Meld, PlayerView, ReactionId } from '@garbage/shared';
import { ALLOWED_CLAIM_WINDOWS_MS, ROUNDS, isJoker, plural } from '@garbage/shared';
import { packsForPlayers } from '@garbage/shared';
import {
  contractFor, findContracts, jokerOptionsFor, swapOptionsAt,
} from '@garbage/engine';
import type { PackId } from '../packs.js';
import { LeaveButton } from './LeaveButton.js';
import { displayHand } from '../handOrder.js';
import { useContractText, useSecondsText, useT } from '../i18n.js';
import { SettingsButton } from './SettingsSheet.js';
import { ReactionBubble, ReactionButton, ReactionPicker, useLiveReactions } from './Reactions.js';
import type { WireAction } from '../protocol.js';
import { CardBack, CardFace, EmptyPile, cardName } from './Card.js';
import { type Asker, ClaimBanner } from './ClaimBanner.js';
import { Hand } from './Hand.js';
import { Melds } from './Melds.js';
import { KickButton } from './KickButton.js';
import { StillThere } from './StillThere.js';
import { JokerEndChooser } from './JokerEndChooser.js';
import { DownChooser } from './DownChooser.js';
import { DiscardPicker } from './DiscardPicker.js';
import { DeadRoundPanel } from './DeadRoundPanel.js';

export type TableProps = {
  view: PlayerView;
  legal: string[];
  claimClosesAt: number | null;
  /** What the last change did — used to announce who won a thrown card. */
  events?: GameEvent[];
  customOrder: string[] | null;
  onAct: (action: WireAction) => void;
  onReorder: (order: string[] | null) => void;
  onHelp: () => void;
  onLeave: () => void;
  pack: PackId;
  /** When the player on turn is taken out for doing nothing. scope.md §6.4. */
  idleOutAt?: number | null;
  /** Absent players the host may remove. */
  kickable?: string[];
  isHost?: boolean;
  onExtendTurn?: () => void;
  onKick?: (id: string) => void;
  /** Host only: change the time to ask, at any point in the game. scope.md §3.5. */
  onClaimTime?: (claimWindowMs: number) => void;
  /** Send a preset reaction. scope.md §8.3. */
  onReact?: (reaction: ReactionId) => void;
  /** Settings — language, sound, how to play, feedback. Replaces the ? button when given. */
  onSettings?: () => void;
};

/** The times to ask the host can pick between, mid-game as in the lobby. */
const CLAIM_TIMES = ALLOWED_CLAIM_WINDOWS_MS;

/**
 * Which melds the selected card could be added to.
 *
 * You may only ever *extend* what is already on the table — going down lays the
 * contract, and after that spare cards join existing melds. There is no way to start
 * a new set or run of your own. scope.md §3.4.
 */
export function layOffTargetsFor(view: PlayerView, card: Card | null): string[] {
  const self = view.players.find((p) => p.id === view.you);
  if (!card || !self?.down || view.rules.layOff === 'never') return [];
  if (view.rules.layOff === 'turnAfter' && self.justWentDown) return [];

  return view.melds
    .filter((meld) => view.rules.layOffOnOthers || meld.ownerId === view.you)
    .filter((meld) => {
      // A joker can go on too; the declaration is worked out rather than asked for.
      if (isJoker(card)) return jokerOptionsFor(meld, card, view.rules).length > 0;
      if (meld.kind === 'set') return meld.cards.some((c) => c.rank === card.rank);
      const suit = meld.cards.find((c) => !isJoker(c))?.suit;
      return card.suit === suit;
    })
    .map((meld) => meld.id);
}

/**
 * Every joker swap open to this player, worked out by the engine exactly as the server
 * will judge it.
 *
 * You may take one once you are down, or when taking it is what lets you go down this
 * turn — which is the case that matters, since a wild is usually the card that
 * completes a contract. Sometimes it takes two. scope.md §3.6.
 */
function swapOptionsFor(view: PlayerView) {
  const self = view.players.find((p) => p.id === view.you);
  if (!self) return [];
  return swapOptionsAt({
    playerId: view.you, down: self.down, hand: self.hand ?? [],
    melds: view.melds, round: view.round, rules: view.rules,
  }, self.pendingJokerIds ?? []);
}

/** Melds holding a joker this player could take. */
export function swapTargetsFor(view: PlayerView): string[] {
  return [...new Set(swapOptionsFor(view).map((o) => o.meldId))];
}

/**
 * Everyone asking for the thrown card, in the order the tie-break hands it out: nearest
 * in turn order after the discarder, which is to say starting from whoever it is free
 * for. scope.md §3.5.
 */
export function askersInLine(view: PlayerView): Asker[] {
  const window = view.claimWindow;
  if (!window) return [];
  const seats = view.players.slice().sort((a, b) => a.seat - b.seat);
  const start = seats.find((p) => p.id === window.freeFor)?.seat ?? 0;
  return [...seats.filter((p) => p.seat >= start), ...seats.filter((p) => p.seat < start)]
    .filter((p) => window.claims.includes(p.id))
    .map((p) => ({ id: p.id, name: p.name, you: p.id === view.you }));
}

/** Who won the last thrown card, if the latest change settled one. */
export function claimTaken(events: readonly GameEvent[] | undefined) {
  for (const event of [...(events ?? [])].reverse()) {
    if (event.type === 'CLAIM_WINDOW_CLOSED' && event.winner) {
      return { playerId: event.winner, card: event.card, penalty: event.tookPenalty };
    }
  }
  return null;
}

/** The new time to ask, if the latest change set one. */
export function claimTimeChanged(events: readonly GameEvent[] | undefined): number | null {
  for (const event of [...(events ?? [])].reverse()) {
    if (event.type === 'CLAIM_TIME_CHANGED') return event.claimWindowMs;
  }
  return null;
}

/** How long the "took it" announcement and pulse stay up. */
const TOOK_MS = 2600;

type Stands = Meld['jokers'][number];

/** With aces both ways the same end can come out twice; offer each card once. */
function distinctOptions(options: Stands[]): Stands[] {
  const seen = new Set<string>();
  return options.filter((o) => {
    const key = `${o.rank}-${o.suit ?? ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function Table(props: TableProps) {
  const { view, legal, onAct } = props;
  const t = useT();
  const contractText = useContractText();
  const secondsText = useSecondsText();
  const liveReactions = useLiveReactions();
  const [reacting, setReacting] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  /** A wild waiting to be told which end of a run it goes on. */
  const [jokerChoice, setJokerChoice] = useState<{ meldId: string; options: Stands[] } | null>(null);

  const self = view.players.find((p) => p.id === view.you);
  const hand = useMemo(
    () => displayHand(self?.hand ?? [], props.customOrder),
    [self?.hand, props.customOrder],
  );

  const myTurn = view.turn.playerId === view.you && !view.claimWindow;
  const selectedCard = hand.find((c) => c.id === selected) ?? null;

  const contract = useMemo(() => contractFor(view.round, view.rules), [view.round, view.rules]);
  /**
   * The engine finds the melds, so going down is never a puzzle. When the hand can make
   * the contract only one way it is a single tap; when it can make it several ways the
   * player picks, since that decides what stays in hand. scope.md §3.4. Nothing is
   * offered until the engine allows it, so never before the player has taken a card.
   */
  const mayGoDown = legal.includes('GO_DOWN');
  const goDownOptions = useMemo(
    () => (self && !self.down && mayGoDown ? findContracts(self.hand ?? [], contract, view.rules) : []),
    [self, contract, view.rules, mayGoDown],
  );
  const [choosingDown, setChoosingDown] = useState(false);
  // A choice left open must not reappear on a later turn, or once the hand has changed.
  useEffect(() => setChoosingDown(false), [view.turn.playerId, goDownOptions.length]);

  /*
   * Choosing how deep to dig into the thrown pile. It opens by itself when the player
   * asked for the card during the window, since the pile is now their only draw. §3.4.
   */
  const [digging, setDigging] = useState(false);
  const myDraw = myTurn && view.turn.phase === 'draw';
  useEffect(() => { if (!myDraw) setDigging(false); }, [myDraw]);
  const pickerOpen = myDraw && view.discardTop !== null && view.maxDiscardTake >= 2
    && (digging || view.turn.mustTakeDiscard === true);
  const takeFromDiscard = () => (view.maxDiscardTake >= 2 ? setDigging(true) : onAct({ type: 'DRAW_DISCARD' }));

  const askers = askersInLine(view);
  /** Who just won a thrown card. Kept briefly so everyone sees it go, bots included. */
  const [took, setTook] = useState<ReturnType<typeof claimTaken>>(null);
  useEffect(() => {
    const latest = claimTaken(props.events);
    if (latest) setTook(latest);
  }, [props.events]);
  useEffect(() => {
    if (!took) return undefined;
    const handle = setTimeout(() => setTook(null), TOOK_MS);
    return () => clearTimeout(handle);
  }, [took]);
  /** Everyone is told when the host changes the time to ask. */
  const [newClaimTime, setNewClaimTime] = useState<number | null>(null);
  useEffect(() => {
    const changed = claimTimeChanged(props.events);
    if (changed !== null) setNewClaimTime(changed);
  }, [props.events]);
  useEffect(() => {
    if (newClaimTime === null) return undefined;
    const handle = setTimeout(() => setNewClaimTime(null), TOOK_MS);
    return () => clearTimeout(handle);
  }, [newClaimTime]);
  const [choosingClaimTime, setChoosingClaimTime] = useState(false);

  const nameOf = (id: string) => (id === view.you
    ? t('common.you')
    : view.players.find((p) => p.id === id)?.name ?? t('common.someone'));

  const layOffTargets = layOffTargetsFor(view, selectedCard);
  const swapTargets = swapTargetsFor(view);

  function layOff(meldId: string, chosen?: Stands) {
    if (!selectedCard) return;
    const meld = view.melds.find((m) => m.id === meldId);
    // A joker has to say what it is standing in for. On a set, or at a run's only open
    // end, there is one answer, so just place it. When a run is open at both ends the
    // player picks, because it decides which real cards can follow. scope.md §3.4.
    let stands = chosen;
    if (!stands && meld && isJoker(selectedCard)) {
      const options = distinctOptions(jokerOptionsFor(meld, selectedCard, view.rules));
      if (options.length > 1) {
        setJokerChoice({ meldId, options });
        return;
      }
      stands = options[0];
    }
    setJokerChoice(null);
    onAct({
      type: 'LAY_OFF',
      meldId,
      cardId: selectedCard.id,
      ...(stands ? { joker: { rank: stands.rank, ...(stands.suit ? { suit: stands.suit } : {}) } } : {}),
    });
    setSelected(null);
  }

  function swapJoker(meldId: string) {
    // Give a card the swap is actually allowed with: when it takes two jokers to go
    // down, which card pays for the first can matter.
    const option = swapOptionsFor(view).find((o) => o.meldId === meldId);
    if (!option) return;
    onAct({ type: 'SWAP_JOKER', ...option });
    setSelected(null);
  }

  function discardSelected() {
    if (!selectedCard || !legal.includes('DISCARD')) return;
    onAct({ type: 'DISCARD', cardId: selectedCard.id });
    setSelected(null);
  }

  return (
    <div className="flex h-full flex-col">
      {/* What this round is asking for, always on screen. §8.1 */}
      <header className="flex items-center gap-2 px-3 pt-[calc(0.5rem+var(--pad-top))]">
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-wide text-bone/50">
            {t('table.header', {
              round: view.round, rounds: ROUNDS, packs: packsForPlayers(view.players.length, view.rules.packs),
            })}
          </p>
          <p className="text-sm font-black text-sun" data-testid="contract">
            {contractText(contract)}
          </p>
        </div>
        {/* Everyone can see how long there is to ask; only the host can change it. */}
        <button
          type="button"
          data-testid="claim-time"
          disabled={!props.isHost}
          aria-expanded={props.isHost ? choosingClaimTime : undefined}
          aria-label={t(props.isHost ? 'table.timeToAskChange' : 'table.timeToAsk', {
            time: secondsText(view.rules.claimWindowMs),
          })}
          onClick={() => setChoosingClaimTime(!choosingClaimTime)}
          className={[
            'min-h-10 rounded-full px-3 text-sm font-black',
            props.isHost ? 'bg-white/10 active:bg-white/20' : 'bg-transparent text-bone/60',
            choosingClaimTime ? 'ring-2 ring-sun' : '',
          ].join(' ')}
        >
          ⏱ {t('seconds.short', { count: view.rules.claimWindowMs / 1000 })}
        </button>
        <LeaveButton onLeave={props.onLeave} />
        {props.onSettings ? (
          <SettingsButton onClick={props.onSettings} />
        ) : (
          <button
            type="button"
            data-testid="help-button"
            onClick={props.onHelp}
            aria-label={t('common.howToPlay')}
            className="rounded-full bg-white/10 px-3 text-lg font-black"
          >
            ?
          </button>
        )}
      </header>

      {props.isHost && choosingClaimTime && (
        <div className="flex flex-wrap items-center justify-end gap-2 px-3 pt-1" data-testid="claim-time-choices">
          <span className="text-xs text-bone/60">{t('rules.timeToAsk')}</span>
          {CLAIM_TIMES.map((ms) => (
            <button
              key={ms}
              type="button"
              data-testid={`claim-time-${ms / 1000}`}
              aria-pressed={view.rules.claimWindowMs === ms}
              onClick={() => {
                if (ms !== view.rules.claimWindowMs) props.onClaimTime?.(ms);
                setChoosingClaimTime(false);
              }}
              className={[
                'min-h-10 rounded-xl px-3 text-sm font-bold',
                view.rules.claimWindowMs === ms ? 'bg-sun text-ink' : 'bg-white/10',
              ].join(' ')}
            >
              {t('seconds.short', { count: ms / 1000 })}
            </button>
          ))}
        </div>
      )}

      {/* Opponents */}
      {/* py, not pt: a scrolling row clips its own overflow, so the ring on whoever
          is on turn needs room underneath it. */}
      {props.onReact && reacting && (
        <ReactionPicker
          className="px-3 pt-1"
          onReact={(reaction) => {
            props.onReact?.(reaction);
            setReacting(false);
          }}
        />
      )}

      <div className="flex gap-2 overflow-x-auto px-3 py-2" data-testid="opponents">
        {props.onReact && (
          <ReactionButton
            open={reacting}
            onToggle={() => setReacting(!reacting)}
            {...(liveReactions.get(view.you) ? { mine: liveReactions.get(view.you)! } : {})}
          />
        )}
        {view.players.filter((p) => p.id !== view.you).map((player) => {
          const asking = view.claimWindow?.claims.includes(player.id) ?? false;
          const won = took?.playerId === player.id;
          const reaction = liveReactions.get(player.id);
          return (
          <div
            key={`${player.id}-${won ? 'won' : ''}`}
            data-testid={`opponent-${player.id}`}
            data-asking={asking || undefined}
            data-won={won || undefined}
            className={[
              'relative shrink-0 rounded-xl px-2 py-1 min-w-20',
              won ? 'claim-won bg-sun/30 ring-2 ring-sun'
                : asking ? 'bg-violet-400/25 ring-2 ring-violet-300'
                  : view.turn.playerId === player.id ? 'bg-sun/20 ring-2 ring-sun' : 'bg-black/25',
            ].join(' ')}
          >
            {reaction && <ReactionBubble reaction={reaction} className="absolute -top-1 right-0" />}
            <p className="truncate text-xs font-bold">
              {player.name}
              {asking && !won && <span className="ml-1 text-violet-200">🙋</span>}
              {won && <span className="ml-1">✋</span>}
            </p>
            <p className="text-[10px] text-bone/60">
              <span data-testid={`hand-count-${player.id}`}>✋ {player.handCount}</span>
              {player.down && <span className="ml-1 text-sun">{t('table.opponentDown')}</span>}
              {!player.connected && <span className="ml-1 text-coral">{t('table.opponentOff')}</span>}
              {props.kickable?.includes(player.id) && <span className="ml-1 text-coral">{t('table.opponentAway')}</span>}
            </p>
            {props.isHost && props.kickable?.includes(player.id) && (
              <KickButton name={player.name} onKick={() => props.onKick?.(player.id)} />
            )}
          </div>
          );
        })}
      </div>

      {took && (
        <p
          key={`${took.playerId}-${took.card.id}`}
          data-testid="claim-taken"
          aria-live="polite"
          className="claim-won mx-auto mt-1 rounded-full bg-sun px-3 py-1 text-center text-sm font-black text-ink"
        >
          {took.playerId === view.you
            ? t('table.youGot', { card: cardName(took.card, t('card.joker')) })
            : t('table.theyGot', { name: nameOf(took.playerId), card: cardName(took.card, t('card.joker')) })}
          {took.penalty && <span className="font-bold opacity-70"> {t('table.extraCard')}</span>}
        </p>
      )}

      {newClaimTime !== null && (
        <p
          data-testid="claim-time-changed"
          aria-live="polite"
          className="mx-auto mt-1 rounded-full bg-white/15 px-3 py-1 text-center text-sm font-bold"
        >
          ⏱ {t('table.claimTimeChanged', { time: secondsText(newClaimTime) })}
        </p>
      )}

      <p className="px-3 pt-2 text-center text-sm font-bold" data-testid="turn-banner">
        {view.claimWindow
          ? <span className="text-sun">
            {/* Once a card has been taken, who threw the one beneath no longer matters. */}
            {view.claimWindow.discardedBy && !view.claimWindow.uncovered
              ? view.claimWindow.discardedBy === view.you
                ? t('table.youThrew')
                : t('table.theyThrew', { name: nameOf(view.claimWindow.discardedBy) })
              : t('table.upForGrabs')}
          </span>
          : myTurn
            ? <span className="text-sun">{view.turn.phase === 'draw' ? t('table.yourTurnTake') : t('table.yourTurn')}</span>
            : <span className="text-bone/60">
              {t('table.theirTurn', {
                name: view.players.find((p) => p.id === view.turn.playerId)?.name ?? t('common.someone'),
              })}
            </span>}
      </p>

      {myTurn && (
        <StillThere idleOutAt={props.idleOutAt ?? null} onExtend={() => props.onExtendTurn?.()} />
      )}

      {/* The melds are the only part of the table that scrolls. Everything below —
          piles, going down, the hand — stays docked, however many players are down. */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid="melds-scroll">
        <Melds
          view={view}
          pack={props.pack}
          selectedCardId={selected}
          layOffTargets={layOffTargets}
          swapTargets={myTurn ? swapTargets : []}
          onLayOff={layOff}
          onSwapJoker={swapJoker}
        />
      </div>

      <DeadRoundPanel view={view} onVote={(choice) => onAct({ type: 'DEAD_ROUND_VOTE', choice })} />

      {/* While a card is on offer the piles are dead — you cannot draw — so the
          banner takes their place rather than covering the hand. */}
      {view.claimWindow ? (
        <ClaimBanner
          card={view.claimWindow.card}
          pack={props.pack}
          durationMs={view.claimWindow.durationMs}
          closesAt={props.claimClosesAt}
          youMayClaim={view.claimWindow.youMayClaim}
          costsPenalty={view.claimWindow.costsPenalty}
          alreadyClaimed={view.claimWindow.claims.includes(view.you)}
          askers={askers}
          thrownBy={view.claimWindow.discardedBy && !view.claimWindow.uncovered
            ? nameOf(view.claimWindow.discardedBy)
            : null}
          thrownByYou={view.claimWindow.discardedBy === view.you && !view.claimWindow.uncovered}
          uncovered={view.claimWindow.uncovered}
          onClaim={() => onAct({ type: 'CLAIM', cardId: view.claimWindow!.card.id })}
        />
      ) : pickerOpen ? (
        <DiscardPicker
          reach={view.discardReach}
          penaltyApplies={view.rules.claimPenalty === 'oneCard'}
          pack={props.pack}
          mustTake={view.turn.mustTakeDiscard === true}
          onTake={(count) => {
            setDigging(false);
            onAct(count > 1 ? { type: 'DRAW_DISCARD', count } : { type: 'DRAW_DISCARD' });
          }}
          onCancel={() => setDigging(false)}
        />
      ) : choosingDown && myTurn && goDownOptions.length > 1 ? (
        <DownChooser
          options={goDownOptions}
          hand={self?.hand ?? []}
          pack={props.pack}
          onPick={(melds) => {
            setChoosingDown(false);
            setSelected(null);
            onAct({ type: 'GO_DOWN', melds });
          }}
          onCancel={() => setChoosingDown(false)}
        />
      ) : jokerChoice && selectedCard && view.melds.some((m) => m.id === jokerChoice.meldId) ? (
        <JokerEndChooser
          meld={view.melds.find((m) => m.id === jokerChoice.meldId)!}
          joker={selectedCard}
          options={jokerChoice.options}
          rules={view.rules}
          onPick={(stands) => layOff(jokerChoice.meldId, stands)}
          onCancel={() => setJokerChoice(null)}
        />
      ) : (
        <div className="flex items-end justify-center gap-6 px-3 pb-1" data-testid="piles">
          <Pile
            label={t('table.stock', { count: view.stockCount })}
            testId="stock"
            enabled={myTurn && legal.includes('DRAW_STOCK')}
            onClick={() => onAct({ type: 'DRAW_STOCK' })}
          >
            {view.stockCount > 0 ? <CardBack pack={props.pack} /> : <EmptyPile />}
          </Pile>

          <Pile
            label={selectedCard && legal.includes('DISCARD')
              ? t('table.throwHere')
              : t('table.thrown', { count: view.discardCount })}
            testId="discard"
            enabled={myTurn && (legal.includes('DRAW_DISCARD') || (selectedCard !== null && legal.includes('DISCARD')))}
            onClick={() => (selectedCard && legal.includes('DISCARD')
              ? discardSelected()
              : takeFromDiscard())}
          >
            {view.discardTop ? <CardFace card={view.discardTop} pack={props.pack} /> : <EmptyPile />}
          </Pile>
        </div>
      )}

      {myTurn && goDownOptions.length > 0 && !choosingDown && (
        <button
          type="button"
          data-testid="go-down"
          onClick={() => (goDownOptions.length === 1
            ? onAct({ type: 'GO_DOWN', melds: goDownOptions[0]! })
            : setChoosingDown(true))}
          className="mx-3 mb-1 rounded-2xl bg-sun py-3 text-lg font-black text-ink active:scale-95"
        >
          {t('table.layDown', { contract: contractText(contract) })}
          {goDownOptions.length > 1 && (
            <span className="text-sm font-bold"> {t('table.ways', { count: goDownOptions.length })}</span>
          )}
        </button>
      )}

      {(self?.pendingJokerIds?.length ?? 0) > 0 && (
        <p className="px-4 pb-1 text-center text-xs font-bold text-violet-300" data-testid="pending-joker">
          {plural(t, 'table.pendingJoker', self?.pendingJokerIds?.length ?? 0)}
        </p>
      )}

      <Hand
        cards={hand}
        pack={props.pack}
        onReorder={props.onReorder}
        onSelect={(card) => {
          setJokerChoice(null);
          setSelected(selected === card.id ? null : card.id);
        }}
        selectedId={selected}
        canAct={myTurn}
      />

    </div>
  );
}

function Pile({
  label, testId, enabled, onClick, children,
}: {
  label: string; testId: string; enabled: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        data-testid={`pile-${testId}`}
        disabled={!enabled}
        aria-label={label}
        onClick={onClick}
        className={[
          'rounded-lg transition-transform',
          enabled ? 'ring-4 ring-sun active:scale-95' : 'opacity-70',
        ].join(' ')}
      >
        {children}
      </button>
      <span className="text-[10px] text-bone/60">{label}</span>
    </div>
  );
}
